import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shell } from '../../components/layout/Shell';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { FitBadge } from '../../components/hikari/ScoreBreakdown';
import { toast } from 'sonner';

const STATUSES = ['all', 'applied', 'invite', 'interviewed', 'offered', 'hire'];

const EmployerInbox = () => {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get('/applications/inbox').then((r) => setApps(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const invite = async (candidate_id, job_id) => {
    try {
      await api.post(`/employer/applications/${candidate_id}/${job_id}/action`, { action: 'invite' });
      toast.success('Candidate invited — take the next step from their profile.');
      navigate(`/employer/candidate/${candidate_id}?job=${job_id}`);
    } catch {
      toast.error('Invite failed');
    }
  };

  const filtered = filter === 'all' ? apps : apps.filter((a) => a.status === filter);

  return (
    <Shell>
      <div className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="label-eyebrow text-primary mb-2">APPLICATIONS INBOX</div>
        <h1 className="font-display font-black text-4xl tracking-tighter mb-6">Every applicant, ranked by proof.</h1>

        <div className="flex flex-wrap gap-2 mb-6" data-testid="inbox-filter">
          {STATUSES.map((s) => (
            <Button key={s} size="sm" variant={filter === s ? 'default' : 'outline'} className="rounded-none" onClick={() => setFilter(s)} data-testid={`inbox-filter-${s}`}>
              {s.toUpperCase()} · {s === 'all' ? apps.length : apps.filter((a) => a.status === s).length}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="mono text-sm text-muted-foreground">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="border border-border bg-white p-10 text-center text-sm text-muted-foreground">
            No applications for this filter.
          </div>
        ) : (
          <div className="border border-border" data-testid="inbox-list">
            {filtered.map((a) => (
              <div key={a.id} className="p-5 bg-white border-b border-border grid md:grid-cols-12 gap-4 items-center" data-testid={`inbox-row-${a.id}`}>
                <div className="md:col-span-1">
                  {a.score ? <FitBadge value={a.score.overall_fit} size="sm" /> : <div className="mono text-xs text-muted-foreground">—</div>}
                </div>
                <div className="md:col-span-4 min-w-0">
                  <Link
                    to={`/employer/candidate/${a.candidate_id}?job=${a.job_id}`}
                    className="font-display font-black text-base hover:text-primary underline-offset-4 hover:underline"
                    data-testid={`inbox-open-profile-${a.id}`}
                  >
                    {a.candidate?.name}
                  </Link>
                  <div className="mono text-xs text-muted-foreground truncate">{a.candidate?.headline || a.candidate?.email}</div>
                </div>
                <div className="md:col-span-4 min-w-0">
                  <div className="label-eyebrow text-muted-foreground">FOR</div>
                  <div className="font-medium text-sm truncate">{a.job?.title}</div>
                </div>
                <div className="md:col-span-1 label-eyebrow">{a.status}</div>
                <div className="md:col-span-2 flex flex-wrap gap-1 justify-end">
                  <Link to={`/employer/candidate/${a.candidate_id}?job=${a.job_id}`} data-testid={`inbox-view-profile-${a.id}`}>
                    <Button size="sm" variant="outline" className="rounded-none h-8 text-xs">View profile</Button>
                  </Link>
                  {a.status === 'applied' && (
                    <Button size="sm" className="rounded-none h-8 text-xs" onClick={() => invite(a.candidate_id, a.job_id)} data-testid={`inbox-invite-${a.id}`}>
                      Invite
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Shell>
  );
};

export default EmployerInbox;
